import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Sparkline } from "@/components/alia/sparkline";
import { cn } from "@/lib/utils";

interface MetricCardProps {
  label: string;
  value: number | string;
  hint?: string;
  icon?: LucideIcon;
  tone?: "default" | "brand" | "warning" | "success";
  trend?: number[];
  delta?: number;
  goodWhenUp?: boolean;
}

const TONE_STYLES = {
  default: "text-foreground",
  brand: "text-brand",
  warning: "text-warning",
  success: "text-success",
};

export function MetricCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "default",
  trend,
  delta,
  goodWhenUp = true,
}: MetricCardProps) {
  const [expanded, setExpanded] = useState(false);

  const isUp = delta !== undefined && delta > 0;
  const isDown = delta !== undefined && delta < 0;
  const isNeutral = delta === 0;
  const isGood = isNeutral ? null : goodWhenUp ? isUp : isDown;

  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2 transition-colors hover:border-border-strong">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {Icon && (
            <Icon
              className={cn("size-3.5 shrink-0", TONE_STYLES[tone])}
              aria-hidden
            />
          )}
          <span className="truncate text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </span>
        </div>

        {trend && trend.length > 1 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground"
            aria-label={expanded ? "Ocultar tendencia" : "Ver tendencia"}
          >
            <ChevronDown
              className={cn(
                "size-3 transition-transform",
                expanded && "rotate-180"
              )}
            />
          </button>
        )}
      </div>

      <div className="mt-1 flex items-baseline gap-2">
        <span
          className={cn(
            "text-xl font-semibold tracking-tight tabular-nums",
            TONE_STYLES[tone]
          )}
        >
          {value}
        </span>
        {hint && (
          <span className="text-[11px] text-muted-foreground">{hint}</span>
        )}

        {delta !== undefined && (
          <span
            className={cn(
              "ml-auto inline-flex items-center gap-0.5 text-[10px] font-medium tabular-nums",
              isGood === true && "text-success",
              isGood === false && "text-destructive",
              isGood === null && "text-muted-foreground"
            )}
          >
            {isUp && <ArrowUp className="size-2.5" />}
            {isDown && <ArrowDown className="size-2.5" />}
            {isUp || isDown ? Math.abs(delta) : delta}
          </span>
        )}
      </div>

      {/* Expandable sparkline — fades in / out smoothly */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-200",
          expanded ? "mt-2 max-h-12 opacity-100" : "max-h-0 opacity-0"
        )}
      >
        {trend && (
          <div className={cn("-mx-1", TONE_STYLES[tone])}>
            <Sparkline data={trend} width={200} height={32} />
          </div>
        )}
      </div>
    </div>
  );
}
